import React, { memo } from 'react';

import LinkIcon from '@atlaskit/icon/core/link';
import {
	AtlassianIcon,
	BitbucketIcon,
	CompassIcon,
	ConfluenceIcon,
	FocusIcon,
	HomeIcon,
	JiraIcon,
	JiraProductDiscoveryIcon,
	JiraServiceManagementIcon,
	TrelloIcon,
} from '@atlaskit/logo';

import { AdobeSignIcon } from './3p-icons/adobe-sign-icon';
import { AdobeXDIcon } from './3p-icons/adobe-xd-icon';
import { AhaIcon } from './3p-icons/aha-icon';
import { AirtableIcon } from './3p-icons/airtable-icon';
import { AmplitudeIcon } from './3p-icons/amplitude-icon';
import { AsanaIcon } from './3p-icons/asana-icon';
import { AWSS3Icon } from './3p-icons/aws-s3-icon';
import { AzureDevopsIcon } from './3p-icons/azure-devops-icon';
import { BoxIcon } from './3p-icons/box-icon';
import { CanvaIcon } from './3p-icons/canva-icon';
import { DocusignIcon } from './3p-icons/docusign-icon';
import { DovetailIcon } from './3p-icons/dovetail-icon';
import { DropboxIcon } from './3p-icons/dropbox-icon';
import { FigmaIcon } from './3p-icons/figma-icon';
import { FreshserviceIcon } from './3p-icons/freshservice-icon';
import { GitbookIcon } from './3p-icons/gitbook-icon';
import { GithubIcon } from './3p-icons/github-icon';
import { GitlabIcon } from './3p-icons/gitlab-icon';
import { GmailIcon } from './3p-icons/gmail-icon';
import { GoogleCalendarIcon } from './3p-icons/google-calendar-icon';
import { GoogleDriveIcon } from './3p-icons/google-drive-icon';
import { HubSpotIcon } from './3p-icons/hubspot-icon';
import { LaunchDarklyIcon } from './3p-icons/launchdarkly-icon';
import { LoomIcon } from './3p-icons/loom-icon';
import { LucidIcon } from './3p-icons/lucid-icon';
import { MiroIcon } from './3p-icons/miro-icon';
import { MondayIcon } from './3p-icons/monday-icon';
import { MuralIcon } from './3p-icons/mural-icon';
import { NotionIcon } from './3p-icons/notion-icon';
import { OutlookCalendarIcon } from './3p-icons/outlook-calendar-icon';
import { OutlookMailIcon } from './3p-icons/outlook-mail-icon';
import { PagerDutyIcon } from './3p-icons/pagerduty-icon';
import { PipedriveIcon } from './3p-icons/pipedrive-icon';
import { PowerBIIcon } from './3p-icons/powerbi-icon';
import { SalesforceIcon } from './3p-icons/salesforce-icon';
import { SentryIcon } from './3p-icons/sentry-icon';
import { ServiceNowIcon } from './3p-icons/servicenow-icon';
import { SharePointIcon } from './3p-icons/sharepoint-icon';
import { SlackIcon } from './3p-icons/slack-icon';
import { SmartsheetIcon } from './3p-icons/smartsheet-icon';
import { StripeIcon } from './3p-icons/stripe-icon';
import { TeamsIcon } from './3p-icons/teams-icon';
import { TodoistIcon } from './3p-icons/todoist-icon';
import { WebexIcon } from './3p-icons/webex-icon';
import { ZendeskIcon } from './3p-icons/zendesk-icon';
import { ZeplinIcon } from './3p-icons/zeplin-icon';
import { getRovoLogoFromSVG, RovoLogoFromLogo } from './rovo-icon';
import type { Size } from './types';

const AppKeys1P = {
	Atlas: 'Atlas',
	Bitbucket: 'Bitbucket',
	Compass: 'Compass',
	Confluence: 'Confluence',
	Jira: 'Jira',
	JiraProductDiscovery: 'Jira Product Discovery',
	JiraServiceManagement: 'Jira Service Management',
	Trello: 'Trello',
	Focus: 'Mercury',
} as const;

const AppKeys3P = {
	AtlassianWebCrawler: 'Atlassian-web-crawler',
	Figma: 'Figma',
	Github: 'Github',
	GoogleDrive: 'Google',
	GoogleDriveProvider: 'Google Drive provider',
	GoogleDriveSmartLink: 'Google Drive',
	GoogleSheetsSmartLink: 'GoogleDrive',
	Loom: 'Loom',
	OneDrive: 'One Drive',
	Dropbox: 'Dropbox',
	Slack: 'Slack',
	Sharepoint: 'sharepoint',
	Teams: 'teams',
	GoogleCalendar: 'google-calendar',
	OutlookCalendar: 'outlook-calendar',
	OutlookMail: 'outlook-mail',
	Gmail: 'gmail',
	AdobeSign: 'adobe-sign',
	Asana: 'asana',
	Zendesk: 'zendesk',
	Airtable: 'airtable',
	Smartsheet: 'smartsheet',
	Notion: 'notion',
	Miro: 'miro',
	Docusign: 'docusign',
	Lucid: 'lucid',
	Monday: 'monday',
	AzureDevops: 'azure-devops',
	Gitlab: 'gitlab',
	ConfluenceDC: 'confluence-dc',
	Canva: 'canva',
	Box: 'box',
	Zeplin: 'zeplin',
	ServiceNow: 'servicenow',
	Freshservice: 'freshservice',
	PowerBI: 'power-bi',
	ClickUp: 'clickup',
	Amplitude: 'amplitude',
	Stripe: 'stripe',
	AdobeXD: 'adobexd',
	Aha: 'aha',
	Mural: 'mural',
	Webex: 'webex',
	Todoist: 'todoist',
	PagerDuty: 'pagerduty',
	Pipedrive: 'pipedrive',
	Hubspot: 'hubspot',
	Dovetail: 'dovetail',
	LaunchDarkly: 'launchdarkly',
	Salesforce: 'salesforce',
	Sentry: 'sentry',
	JiraDC: 'jira-dc',
	Gitbook: 'gitbook',
	AWSS3: 'aws-s3',
} as const;

const app1PIcons = {
	[AppKeys1P.Confluence]: RovoLogoFromLogo(ConfluenceIcon, 'Confluence'),
	[AppKeys1P.Atlas]: RovoLogoFromLogo(HomeIcon, 'Home'),
	[AppKeys1P.Jira]: RovoLogoFromLogo(JiraIcon, 'Jira'),
	[AppKeys1P.JiraProductDiscovery]: RovoLogoFromLogo(
		JiraProductDiscoveryIcon,
		'Jira Product Discovery',
	),
	[AppKeys1P.JiraServiceManagement]: RovoLogoFromLogo(
		JiraServiceManagementIcon,
		'Jira Service Management',
	),
	[AppKeys1P.Bitbucket]: RovoLogoFromLogo(BitbucketIcon, 'Bitbucket'),
	[AppKeys1P.Trello]: RovoLogoFromLogo(TrelloIcon, 'Trello'),
	[AppKeys1P.Compass]: RovoLogoFromLogo(CompassIcon, 'Compass'),
	[AppKeys1P.Focus]: RovoLogoFromLogo(FocusIcon, 'Focus'),
};

const app3PIcons = {
	[AppKeys3P.AdobeXD]: getRovoLogoFromSVG(AdobeXDIcon, 'Adobe XD'),
	[AppKeys3P.AdobeSign]: getRovoLogoFromSVG(AdobeSignIcon, 'Adobe Sign'),
	[AppKeys3P.Aha]: getRovoLogoFromSVG(AhaIcon, 'Aha'),
	[AppKeys3P.Airtable]: getRovoLogoFromSVG(AirtableIcon, 'Airtable'),
	[AppKeys3P.Amplitude]: getRovoLogoFromSVG(AmplitudeIcon, 'Amplitude'),
	[AppKeys3P.Asana]: getRovoLogoFromSVG(AsanaIcon, 'Asana'),
	[AppKeys3P.AzureDevops]: getRovoLogoFromSVG(AzureDevopsIcon, 'Azure Devops'),
	[AppKeys3P.Box]: getRovoLogoFromSVG(BoxIcon, 'Box'),
	[AppKeys3P.Canva]: getRovoLogoFromSVG(CanvaIcon, 'Canva'),
	[AppKeys3P.Docusign]: getRovoLogoFromSVG(DocusignIcon, 'Docusign'),
	[AppKeys3P.Dovetail]: getRovoLogoFromSVG(DovetailIcon, 'Dovetail'),
	[AppKeys3P.Dropbox]: getRovoLogoFromSVG(DropboxIcon, 'Dropbox'),
	[AppKeys3P.Figma]: getRovoLogoFromSVG(FigmaIcon, 'Figma'),
	[AppKeys3P.Freshservice]: getRovoLogoFromSVG(FreshserviceIcon, 'Freshservice'),
	[AppKeys3P.Gmail]: getRovoLogoFromSVG(GmailIcon, 'Gmail'),
	[AppKeys3P.Github]: getRovoLogoFromSVG(GithubIcon, 'Github'),
	[AppKeys3P.Gitlab]: getRovoLogoFromSVG(GitlabIcon, 'Gitlab'),
	[AppKeys3P.GoogleCalendar]: getRovoLogoFromSVG(GoogleCalendarIcon, 'Google Calendar'),
	[AppKeys3P.Hubspot]: getRovoLogoFromSVG(HubSpotIcon, 'Hubspot'),
	[AppKeys3P.Lucid]: getRovoLogoFromSVG(LucidIcon, 'Lucid'),
	[AppKeys3P.Loom]: getRovoLogoFromSVG(LoomIcon, 'Loom'),
	[AppKeys3P.Miro]: getRovoLogoFromSVG(MiroIcon, 'Miro'),
	[AppKeys3P.Monday]: getRovoLogoFromSVG(MondayIcon, 'Monday'),
	[AppKeys3P.Mural]: getRovoLogoFromSVG(MuralIcon, 'Mural'),
	[AppKeys3P.Notion]: getRovoLogoFromSVG(NotionIcon, 'Notion'),
	[AppKeys3P.OutlookCalendar]: getRovoLogoFromSVG(OutlookCalendarIcon, 'Outlook Calendar'),
	[AppKeys3P.OutlookMail]: getRovoLogoFromSVG(OutlookMailIcon, 'Outlook Mail'),
	[AppKeys3P.Pipedrive]: getRovoLogoFromSVG(PipedriveIcon, 'Pipedrive'),
	[AppKeys3P.Salesforce]: getRovoLogoFromSVG(SalesforceIcon, 'Salesforce'),
	[AppKeys3P.Sentry]: getRovoLogoFromSVG(SentryIcon, 'Sentry'),
	[AppKeys3P.Sharepoint]: getRovoLogoFromSVG(SharePointIcon, 'Sharepoint'),
	[AppKeys3P.Slack]: getRovoLogoFromSVG(SlackIcon, 'Slack'),
	[AppKeys3P.Smartsheet]: getRovoLogoFromSVG(SmartsheetIcon, 'Smartsheet'),
	[AppKeys3P.Stripe]: getRovoLogoFromSVG(StripeIcon, 'Stripe'),
	[AppKeys3P.Teams]: getRovoLogoFromSVG(TeamsIcon, 'Teams'),
	[AppKeys3P.Todoist]: getRovoLogoFromSVG(TodoistIcon, 'Todoist'),
	[AppKeys3P.Webex]: getRovoLogoFromSVG(WebexIcon, 'Webex'),
	[AppKeys3P.Zeplin]: getRovoLogoFromSVG(ZeplinIcon, 'Zeplin'),
	[AppKeys3P.Zendesk]: getRovoLogoFromSVG(ZendeskIcon, 'Zendesk'),
	[AppKeys3P.Gitbook]: getRovoLogoFromSVG(GitbookIcon, 'Gitbook'),
	[AppKeys3P.AtlassianWebCrawler]: RovoLogoFromLogo(AtlassianIcon, 'Atlassian Web Crawler'),
	[AppKeys3P.ConfluenceDC]: RovoLogoFromLogo(ConfluenceIcon, 'Confluence'),
	[AppKeys3P.JiraDC]: RovoLogoFromLogo(JiraIcon, 'Jira'),
	[AppKeys3P.LaunchDarkly]: getRovoLogoFromSVG(LaunchDarklyIcon, 'LaunchDarkly'),
	[AppKeys3P.PagerDuty]: getRovoLogoFromSVG(PagerDutyIcon, 'PagerDuty'),
	[AppKeys3P.PowerBI]: getRovoLogoFromSVG(PowerBIIcon, 'PowerBI'),
	[AppKeys3P.ServiceNow]: getRovoLogoFromSVG(ServiceNowIcon, 'ServiceNow'),
	[AppKeys3P.AWSS3]: getRovoLogoFromSVG(AWSS3Icon, 'AWSS3'),
	[AppKeys3P.GoogleDrive]: getRovoLogoFromSVG(GoogleDriveIcon, 'GoogleDrive'),
	[AppKeys3P.GoogleDriveProvider]: getRovoLogoFromSVG(GoogleDriveIcon, 'GoogleDrive'),
	[AppKeys3P.GoogleDriveSmartLink]: getRovoLogoFromSVG(GoogleDriveIcon, 'GoogleDrive'),
	[AppKeys3P.GoogleSheetsSmartLink]: getRovoLogoFromSVG(GoogleDriveIcon, 'GoogleDrive'),
};

const appIcons = {
	...app1PIcons,
	...app3PIcons,
};

export const Apps: {
	AdobeSign: 'adobe-sign';
	AdobeXD: 'adobexd';
	Aha: 'aha';
	Airtable: 'airtable';
	Amplitude: 'amplitude';
	Asana: 'asana';
	Atlas: 'Atlas';
	AtlassianWebCrawler: 'Atlassian-web-crawler';
	AWSS3: 'aws-s3';
	AzureDevops: 'azure-devops';
	Bitbucket: 'Bitbucket';
	Box: 'box';
	Canva: 'canva';
	ClickUp: 'clickup';
	Compass: 'Compass';
	Confluence: 'Confluence';
	ConfluenceDC: 'confluence-dc';
	Docusign: 'docusign';
	Dovetail: 'dovetail';
	Dropbox: 'Dropbox';
	Figma: 'Figma';
	Focus: 'Mercury';
	Freshservice: 'freshservice';
	Gitbook: 'gitbook';
	Github: 'Github';
	Gitlab: 'gitlab';
	Gmail: 'gmail';
	GoogleCalendar: 'google-calendar';
	GoogleDrive: 'Google';
	GoogleDriveProvider: 'Google Drive provider';
	GoogleDriveSmartLink: 'Google Drive';
	GoogleSheetsSmartLink: 'GoogleDrive';
	Hubspot: 'hubspot';
	Jira: 'Jira';
	JiraDC: 'jira-dc';
	JiraProductDiscovery: 'Jira Product Discovery';
	JiraServiceManagement: 'Jira Service Management';
	LaunchDarkly: 'launchdarkly';
	Loom: 'Loom';
	Lucid: 'lucid';
	Miro: 'miro';
	Monday: 'monday';
	Mural: 'mural';
	Notion: 'notion';
	OneDrive: 'One Drive';
	OutlookCalendar: 'outlook-calendar';
	OutlookMail: 'outlook-mail';
	PagerDuty: 'pagerduty';
	Pipedrive: 'pipedrive';
	PowerBI: 'power-bi';
	Salesforce: 'salesforce';
	Sentry: 'sentry';
	ServiceNow: 'servicenow';
	Sharepoint: 'sharepoint';
	Slack: 'Slack';
	Smartsheet: 'smartsheet';
	Stripe: 'stripe';
	Teams: 'teams';
	Todoist: 'todoist';
	Trello: 'Trello';
	Webex: 'webex';
	Zendesk: 'zendesk';
	Zeplin: 'zeplin';
} = {
	...AppKeys1P,
	...AppKeys3P,
};

export type AppKeys = (typeof Apps)[keyof typeof Apps];

const FallbackIcon = RovoLogoFromLogo(LinkIcon);

export const AppIcon: React.NamedExoticComponent<{
	app: string | null;
	fallbackIcon?: React.ReactNode;
	size: Size;
}> = memo(function AppIcon({
	app,
	size,
	fallbackIcon,
}: {
	app: string | null;
	fallbackIcon?: React.ReactNode;
	size: Size;
}) {
	if (!app) {
		return fallbackIcon ? fallbackIcon : <FallbackIcon size={size} label="" />;
	}

	const appKey = Object.values(Apps).find((value) => value.toLowerCase() === app.toLowerCase());
	const Icon = appKey ? appIcons[appKey as keyof typeof appIcons] : null;
	return Icon ? (
		<Icon size={size} testId="app-icon" />
	) : fallbackIcon ? (
		fallbackIcon
	) : (
		<FallbackIcon size={size} label="" />
	);
});
